import {
  FetchImportExportHistoriesArgs,
  useGetImportExportHistories,
} from '@/context/services/import_export_histories/ImportExportHistories.service';
import type { ImportExportHistoriesTableProps } from '@/features/import_export_histories/Table';
import { useTranslation } from '@/i18n';
import { useMemo } from 'react';

type Props = {
  searchQuery: FetchImportExportHistoriesArgs;
  isPageLoading?: boolean;
};

/**
 * Check if search query has any search conditions.
 * Returns true if no search conditions are applied (excluding sort_by, page, and per).
 */
export const hasNoSearchConditions = (
  query: FetchImportExportHistoriesArgs
): boolean => {
  // Check only search-related fields, excluding pagination and sorting
  const searchConditionFields: (keyof FetchImportExportHistoriesArgs)[] = [
    'type',
    'source',
    'office_member_ids',
    'submitted_at_from',
    'submitted_at_to',
  ];

  return searchConditionFields.every((field) => {
    const value = query[field];
    return !value || (Array.isArray(value) && value.length === 0);
  });
};

/**
 * Hook for ImportExportHistory table data and action handlers.
 */
export const useImportExportHistoriesTable = ({
  searchQuery,
  isPageLoading: isPageLoadingProp,
}: Props) => {
  const { t } = useTranslation();
  const { data, pagination } = useGetImportExportHistories(searchQuery);

  const table = useMemo<ImportExportHistoriesTableProps>(() => {
    const noSearchConditions = hasNoSearchConditions(searchQuery);
    return {
      data: data || [],
      emptyType: noSearchConditions ? 'object' : 'search',
      emptyText: noSearchConditions
        ? t('import_export_history_list_empty_message')
        : t('import_export_history_list_no_search_results'),
      loading: isPageLoadingProp ?? false,
    };
  }, [data, isPageLoadingProp, searchQuery, t]);

  return {
    table,
    pagination,
  };
};
